/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = undefined;
import Link from "next/link";
import {Image} from "/lib/Image";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    strong: "strong",
    ul: "ul",
    li: "li",
    h3: "h3",
    a: "a"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Concurrency management is complex"
    }), "\n", _jsx(_components.p, {
      children: "Even when you know what you are doing, concurrency management requires to make choices of underlying systems, provision resources and\nconsider unexpected nuances during design and implementation.\nThe more experienced engineers we interviewed expressed familiarity with the problem and idicated having implemented a semaphore or similar\nqueing limiter-buffer ad-hoc solution in past. All of them noted the complexity involved and the number of iterations required to resolve to a working solution."
    }), "\n", _jsx(_components.p, {
      children: "Thus, familiarity with EDA patterns and tools doesn't help the fact that someone needs to build the correct solution, make sure it works and then maintain it.\nThis is even more true when the timeline is tight and the developers are busy with the actual business logic. Which is almost always the case."
    }), "\n", _jsx(_components.p, {
      children: "WebSemaphore is a simple, cloud-native, IaC-style, developer-oriented tool that solves a set of concerns related to concurrency, synchronization and optimiation\nissues instantly, saving your organization weeks to months of costly development and keeping your team focused on their real business case."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Key features:"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Coordination and optimization of concurrent processes and shared resources"
      }), "\n", _jsx(_components.li, {
        children: "Safety of concurrent operations in distributed applications"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Business benefits:"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Business continuity and disaster recovery"
      }), "\n", _jsx(_components.li, {
        children: "Optimized spend vs resource utilization ratio"
      }), "\n", _jsx(_components.li, {
        children: "Reduced energy consumption and emissions"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Technical advantages:"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Developer-oriented SaaS / IaC"
      }), "\n", _jsx(_components.li, {
        children: "Instant cloud-scale semaphores with zero setup, configuration, provisioning or learning curve expenses"
      }), "\n", _jsx(_components.li, {
        children: "Async/Sync operating modes"
      }), "\n", _jsx(_components.li, {
        children: "REST/WebSockets to support multiple integration scenarios"
      }), "\n", _jsx(_components.li, {
        children: "Javascript-based lightweight mappings"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Concerns addressed"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Near real-time, data intensive / long running flows"
      }), "\n", _jsx(_components.li, {
        children: "Spiky/seasonal demand"
      }), "\n", _jsx(_components.li, {
        children: "Ad hoc concurrency requirements implied by capacity, hardware, SLAs or logical/algorithmic constraints"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Application Domains"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Enterprise / B2B / B2G integrations"
      }), "\n", _jsx(_components.li, {
        children: "Near-realtime analytics"
      }), "\n", _jsx(_components.li, {
        children: "MLOps pipelines"
      }), "\n", _jsx(_components.li, {
        children: "Workflow management"
      }), "\n", _jsx(_components.li, {
        children: "IoT"
      }), "\n", _jsx(_components.li, {
        children: "Digital factory, twinning"
      }), "\n", _jsx(_components.li, {
        children: "Smart cities"
      }), "\n", _jsx(_components.li, {
        children: "Any domain with fluctuating traffic, expected availability limitations or specific exclusivity/concurrency requirements"
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Integration scenarios"
    }), "\n", _jsx(_components.p, {
      children: "Whether you are the provider or the consumer, WebSemaphore will help amortize and optimize the API communication."
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Consumers implementing WebSemaphore gain a simple resolution of SLA limitations and API outages"
      }), "\n", _jsx(_components.li, {
        children: "Providers implementing WebSemaphore in front of their critical APIs will be able to accept requests for processing even when their systems are overwhelmed or go down"
      }), "\n", _jsx(_components.li, {
        children: "Both above points are valid for communication internal and external to the organization."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Below are the main integration scenario types. The diagrams below focus on async, EDA-style operating mode which provides most\nadvantages."
    }), "\n", _jsx(_components.h3, {
      children: "Provider is external to consumer"
    }), "\n", _jsx(_components.p, {
      children: "A very common case is needing to access an external provider, such as a CRM or a government database.\nSuch services can introduce artificial license-based limits that are not noticeable at low traffic,\nbut become a challenge as traffic is increasing."
    }), "\n", _jsx(Image, {
      src: "/scenarios/scenario1.svg",
      style: {
        width: '100%'
      }
    }), "\n", _jsx(_components.h3, {
      children: "Provider is internal to consumer"
    }), "\n", _jsx(_components.p, {
      children: "There are many reasons for the need to limit internal traffic to a manageable volume.\nSome example include the number of maximum concurrent lambdas (1000) or concurrent connections to an RDS database (also 1000).\nThis scenario also includes access to hardware / IoT devices manufacturing machinery and other internal resources."
    }), "\n", _jsx(Image, {
      src: "/scenarios/scenario2.svg",
      style: {
        width: '100%'
      }
    }), "\n", _jsx(_components.h3, {
      children: "Provider implements WebSemaphore"
    }), "\n", _jsx(_components.p, {
      children: "Suited for API providers that want to maintain high availability regardless of their backend status.\nIn this scenario, WebSemaphore helps the provider:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Improve service availability."
      }), "\n", _jsx(_components.li, {
        children: "Limit actual throughput without rejecting the requests altogether, while helping the consumer avoid retries and improving the overall consumer experience."
      }), "\n", _jsx(_components.li, {
        children: "Do more business by processing more requests while staying in control of the resource utilization / cost ratio"
      }), "\n"]
    }), "\n", _jsx(Image, {
      src: "/scenarios/scenario3.svg",
      style: {
        width: '100%'
      }
    }), "\n", _jsx(_components.h3, {
      children: "Unique concurrency requirements"
    }), "\n", _jsx(_components.p, {
      children: "Sometimes a whole flow needs to be performed in such a way that no more than one or N such flows should happen at the same time.\nThis is true for both physical devices and ephemeral resources such as AWS Lambdas, limited to 1000 per region in a single account."
    }), "\n", _jsx(_components.h3, {
      children: "See also"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "/docs/v1/concepts/scenarios-integration",
          children: "Integration scenarios"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "/docs/v1/concepts/operating-modes",
          children: "Operating Modes"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "/docs/v1/concepts/protocols",
          children: "Protocols"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "/demos/simulation",
          children: "Simulation of API traffic amortization"
        })
      }), "\n", _jsxs(_components.li, {
        children: ["Usage: ", _jsx(_components.a, {
          href: "/docs/v1/usage/rest",
          children: "REST"
        }), " and ", _jsx(_components.a, {
          href: "/docs/v1/usage/websockets",
          children: "Websockets"
        })]
      }), "\n"]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
